import type { DateValue } from "@internationalized/date";
import { Package, TrendUp01 } from "@untitledui/icons";
import type { PropsWithChildren } from "react";
import { type FilterRangeData, useProductFilterRanges } from "@/api/filter-ranges";
import { CategoryFilter } from "@/components/filters/extra-filters";
import { PillField } from "@/components/filters/pill-field";
import { GmvRangeFilter, PerformanceSimpleRange } from "@/components/filters/range-filters";
import { FilterPanelContainer, FilterPanelHeader, FilterSection } from "@/components/filters/shared";
import { Accordion } from "@/components/ui/accordion";
import { Form, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { STOCK_STATUS_LABELS, STOCK_STATUSES, type StockStatus } from "@/lib/products";
import { useFilters } from "@/providers/filter-provider";

type ProductFilterPanelProps = {
  open: boolean;
  close: () => void;
  clearAll?: () => void;
  dateRange?: { start?: DateValue; end?: DateValue } | null;
} & PropsWithChildren;

export const ProductFilterPanel = ({ open, close, clearAll, dateRange }: ProductFilterPanelProps) => {
  const { reset } = useFilters();
  const { data: filterRanges } = useProductFilterRanges({ range: dateRange ?? null, enabled: open });

  const handleClear = () => {
    clearAll?.();
    reset();
  };

  return (
    <FilterPanelContainer open={open}>
      <FilterPanelHeader onClear={handleClear} onClose={close} />
      <div className="flex-1 overflow-y-auto pt-4 px-6 pb-6">
        <ProductFilters filterRanges={filterRanges} />
      </div>
    </FilterPanelContainer>
  );
};

// =============================================================================
// MAIN PRODUCT FILTERS COMPONENT
// =============================================================================

type ProductFiltersProps = {
  filterRanges?: { gmv: FilterRangeData };
};

const ProductFilters = ({ filterRanges }: ProductFiltersProps) => {
  const { form, isActive } = useFilters();

  return (
    <Form {...form}>
      <div className="flex flex-col gap-6">
        {/* Total GMV */}
        {isActive("minGmv") && isActive("maxGmv") && filterRanges?.gmv && filterRanges.gmv.max > 0 && (
          <GmvRangeFilter
            form={form}
            rangeData={filterRanges.gmv}
            label="Total GMV"
            tooltip="Gross Merchandise Value"
            description="Total revenue generated from this product across all videos."
          />
        )}

        {/* Stock Status */}
        {isActive("stockStatus") && (
          <FormField
            control={form.control}
            name="stockStatus"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2">
                <FormLabel className="text-sm text-tertiary">Stock Status</FormLabel>
                <PillField
                  value={field.value}
                  options={STOCK_STATUSES}
                  onChange={field.onChange}
                  getLabel={(option) => STOCK_STATUS_LABELS[option as StockStatus] ?? option}
                />
              </FormItem>
            )}
          />
        )}

        {/* Expandable Sections */}
        <Accordion type="single" collapsible className="w-full">
          {/* Performance Section */}
          {(isActive("minUnitsSold") || isActive("minVideos") || isActive("minAov")) && (
            <FilterSection id="performance" icon={<TrendUp01 className="w-5 h-5" />} title="Performance">
              {isActive("minUnitsSold") && isActive("maxUnitsSold") && (
                <PerformanceSimpleRange
                  form={form}
                  label="Unit Sales"
                  tooltip="Total Units Sold"
                  description="Number of units sold for this product."
                  minField="minUnitsSold"
                  maxField="maxUnitsSold"
                  min={0}
                  max={5000}
                  format="number"
                />
              )}

              {isActive("minVideos") && isActive("maxVideos") && (
                <PerformanceSimpleRange
                  form={form}
                  label="Videos"
                  tooltip="Video Count"
                  description="Number of videos featuring this product."
                  minField="minVideos"
                  maxField="maxVideos"
                  min={0}
                  max={500}
                  format="number"
                />
              )}

              {isActive("minAov") && isActive("maxAov") && (
                <PerformanceSimpleRange
                  form={form}
                  label="AOV"
                  tooltip="Average Order Value"
                  description="Average value of orders containing this product."
                  minField="minAov"
                  maxField="maxAov"
                  min={0}
                  max={100}
                  format="currency"
                />
              )}
            </FilterSection>
          )}

          {/* Product Details Section */}
          {(isActive("minPrice") || isActive("minStockLevel") || isActive("category")) && (
            <FilterSection id="product-details" icon={<Package className="w-5 h-5" />} title="Product Details">
              {isActive("minPrice") && isActive("maxPrice") && (
                <PerformanceSimpleRange
                  form={form}
                  label="Price"
                  tooltip="Product Price"
                  description="Product selling price."
                  minField="minPrice"
                  maxField="maxPrice"
                  min={0}
                  max={100}
                  format="currency"
                />
              )}

              {isActive("minStockLevel") && isActive("maxStockLevel") && (
                <PerformanceSimpleRange
                  form={form}
                  label="Stock Level"
                  tooltip="Inventory Quantity"
                  description="Current inventory quantity."
                  minField="minStockLevel"
                  maxField="maxStockLevel"
                  min={0}
                  max={5000}
                  format="number"
                />
              )}

              {isActive("category") && (
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => <CategoryFilter field={field} />}
                />
              )}
            </FilterSection>
          )}
        </Accordion>
      </div>
    </Form>
  );
};
